import {useEffect, useRef, useState} from 'react';
import {DEEP_CLEAN_ITEMS, statusColor} from './collaborationTypes';
import {EvidenceUpload, PhotoGallery} from './EvidencePhotos';
import './collaboration.css';

export function TaskWorkItems({task, api, canEdit = true, canManage = false, onChanged, onBusy}) {
    const [photos, setPhotos] = useState([]), [photoError, setPhotoError] = useState('');
    const [uploading, setUploading] = useState(false), [busy, setBusy] = useState(false);
    const [code, setCode] = useState('GENERAL'), [description, setDescription] = useState(''), [error, setError] = useState('');
    const running = useRef(false);
    const editable = canEdit && task.can_execute !== false && task.status !== 'Cancelled';
    const manageable = canManage && task.can_manage_items === true;
    const completed = task.items.filter(item => item.status === 'DONE').length;
    useEffect(() => {
        const abort = new AbortController();
        setPhotoError('');
        api(`/tasks/${task.job_id}/photos`, 'GET', undefined, abort.signal).then(result => {
            if (!abort.signal.aborted) setPhotos(result);
        }).catch(e => {if (!abort.signal.aborted) setPhotoError(e.message);});
        return () => abort.abort();
    }, [api, task.job_id]);
    useEffect(() => {onBusy?.(busy || uploading);}, [busy, uploading, onBusy]);
    async function mutate(operation) {
        if (running.current || uploading) return;
        running.current = true; setBusy(true);
        try {return await operation();}
        finally {running.current = false; setBusy(false);}
    }
    async function addItem() {
        if (!manageable || !description.trim()) return;
        setError('');
        try {
            await mutate(async () => {
                const next = await api(`/tasks/${task.job_id}/items`, 'POST', {item_code: code, description});
                setDescription(''); onChanged(next);
            });
        } catch (e) {setError(e.message || 'Unable to add work item.');}
    }
    return <section aria-label="Task work items" className="collaboration-work">
        <p role="status">{completed}/{task.items.length} work items complete</p>
        {task.execution_blocked_reason ? <p>{task.execution_blocked_reason}</p> : null}
        {photoError ? <p role="alert">{photoError}</p> : null}
        {editable ? <fieldset disabled={busy}><EvidenceUpload api={api} path={`/tasks/${task.job_id}/photos`} count={photos.length}
            onPhoto={photo => setPhotos(current => [...current, photo])} onBusy={setUploading}/></fieldset> : null}
        {!task.items.length ? <p>This existing Task has no work items.</p> : null}
        {task.items.map(item => <TaskWorkItem key={item.id} task={task} item={item} api={api} photos={photos}
            disabled={busy || uploading} canEdit={editable} canManage={manageable} mutate={mutate} onChanged={onChanged}/>)}
        {manageable ? <fieldset disabled={busy || uploading || task.items.length >= 100}>
            <legend>Add work item</legend>
            <label>Category<select value={code} onChange={event => setCode(event.target.value)}>
                <option value="GENERAL">General</option>
                {DEEP_CLEAN_ITEMS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                <option value="CUSTOM">Other deep clean</option>
            </select></label>
            <label>Description<textarea maxLength={4000} value={description} onChange={event => setDescription(event.target.value)}/></label>
            <button type="button" disabled={!description.trim()} onClick={addItem}>Add item</button>
            {task.items.length >= 100 ? <p>At most 100 work items per Task.</p> : null}
        </fieldset> : null}
        {error ? <p role="alert">{error}</p> : null}
    </section>;
}

function TaskWorkItem({task, item, api, photos, disabled, canEdit, canManage, mutate, onChanged}) {
    const [note, setNote] = useState(item.note || ''), [selected, setSelected] = useState(item.photo_ids || []);
    const [description, setDescription] = useState(item.description), [editing, setEditing] = useState(false);
    const [version, setVersion] = useState(item.version), [error, setError] = useState(''), [notice, setNotice] = useState('');
    const [confirmRemove, setConfirmRemove] = useState(false);
    const done = item.status === 'DONE';
    async function submit(method, body) {
        setError(''); setNotice('');
        try {
            await mutate(async () => {
                const next = await api(`/tasks/${task.job_id}/items/${item.id}${method === 'DELETE' ? `?version=${version}` : ''}`, method, body);
                const saved = next.items.find(row => row.id === item.id);
                if (saved) {
                    setVersion(saved.version);
                    if (method === 'PATCH') {setDescription(saved.description); setEditing(false);}
                    if (method === 'PUT') {setNote(saved.note || ''); setSelected(saved.photo_ids || []);}
                }
                setConfirmRemove(false); setNotice(task.status !== 'ReadyForBilling' && next.status === 'ReadyForBilling' ? 'Task complete.' : 'Saved.');
                onChanged(next);
            });
        } catch (e) {setError(e.message || 'Unable to save work item.');}
    }
    async function refresh() {
        setError('');
        try {
            await mutate(async () => {
                const next = await api(`/tasks/${task.job_id}`);
                const latest = next.items.find(row => row.id === item.id);
                if (latest) {
                    setVersion(latest.version);
                    setNotice(`Latest requirement: ${latest.description}. Status: ${latest.status}. Your unsaved draft is retained; review it before saving.`);
                }
                onChanged(next);
            });
        } catch (e) {setError(e.message || 'Unable to refresh work item.');}
    }
    const saveProgress = status => submit('PUT', {status, note, photo_ids: selected, version});
    return <article className="work-item" style={{borderLeftColor: statusColor(item.status)}}>
        <strong>{item.description}</strong><span style={{color: statusColor(item.status)}}>{item.status}</span>
        {item.report_item_id ? <span>Linked Report item #{item.report_item_id}</span> : null}
        <PhotoGallery photos={canEdit ? photos : photos.filter(photo => item.photo_ids?.includes(photo.id))}
            selected={selected} onToggle={canEdit && !disabled
                ? id => setSelected(current => current.includes(id) ? current.filter(value => value !== id) : [...current, id]) : undefined}/>
        {item.completed_at ? <p>Completed {new Date(item.completed_at).toLocaleString()} · Worker {item.completed_by}</p> : null}
        <>
            {canManage ? <fieldset disabled={disabled}>
                {editing ? <>
                    <label>Execution description<textarea maxLength={4000} value={description} onChange={event => setDescription(event.target.value)}/></label>
                    <button type="button" disabled={!description.trim()} onClick={() => submit('PATCH', {description, version})}>Save description</button>
                    <button type="button" onClick={() => {setEditing(false); setDescription(item.description);}}>Cancel edit</button>
                </> : <button type="button" onClick={() => setEditing(true)}>Edit description</button>}
                <button type="button" disabled={task.items.length <= 1} onClick={() => setConfirmRemove(true)}>Remove item</button>
                {confirmRemove ? <div role="alert">
                    <p>Remove this work item? Any linked Report issue will remain unresolved and can be arranged again.</p>
                    <button type="button" onClick={() => submit('DELETE')}>Confirm remove</button>
                    <button type="button" onClick={() => setConfirmRemove(false)}>Keep item</button>
                </div> : null}
            </fieldset> : null}
            {canEdit ? <fieldset disabled={disabled}>
                <label>Completion note / blocked reason<textarea maxLength={4000} value={note} onChange={event => setNote(event.target.value)}/></label>
                <div className="work-actions">
                    <button type="button" onClick={() => saveProgress(undefined)}>{done ? 'Save changes' : 'Save progress'}</button>
                    {done ? <button type="button" onClick={() => saveProgress('OPEN')}>Revert item</button> : <>
                        <button type="button" onClick={() => saveProgress('DONE')}>Complete item</button>
                        <button type="button" disabled={!note.trim()} onClick={() => saveProgress('BLOCKED')}>Blocked</button>
                    </>}
                    {item.status === 'BLOCKED' ? <button type="button" onClick={() => saveProgress('OPEN')}>Resume</button> : null}
                </div>
            </fieldset> : <p>{item.note}</p>}
        </>
        {notice ? <p role="status">{notice}</p> : null}
        {error ? <div role="alert">{error} <button type="button" disabled={disabled} onClick={refresh}>Review latest item</button></div> : null}
    </article>;
}
